import { __rest } from "tslib";
import * as React from 'react';
import { forwardRef, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { startOfDay } from 'date-fns';
import { RouteType } from '@features/price-graph';
import { Datepicker } from '../../Datepicker/components/Datepicker';
import { Provider as PriceMatrixProvider } from '../../PriceMatrix/theme';
import { priceGraphTheme, priceMatrixTheme } from '../theme';
import { DialogAdapter } from './DialogAdapter/DialogAdapter';
import { disableAutocompleteProps, format, mergeCSS, MOBILE_MIN_WIDTH, removeDotsFromDate, TABLET_MIN_WIDTH } from '../../utils';
import { ThemeProvider, useTheme } from '../../theme';
import DummyPriceGraph from '../../Results/components/Results/Calendar/DummyPriceGraph/DummyPriceGraph';
import { CompactMoney } from '../../Money';
import LinearProgress from '@mui/material/LinearProgress';
import EmptyDatesOverlay from './EmptyDatesOverlay/EmptyDatesOverlay';
import FooterButtons from './FooterButtons/FooterButtons';
import { useRenderers } from '../../renderProps';
import { DatepickerTab } from '../../SearchForm/components/SearchForm/Segments/Segment/Datepicker/Datepicker';
import { isMultiCity } from '../../SearchForm/store/segments/selectors';
import { useConfig } from '../../context';
import { useCalendarNormalization } from '../hooks';
import { CdnIcon } from '@shared/ui';
import { TabTypes } from '../types';
const PriceGraph = React.lazy(() => import('@features/price-graph').then(m => ({ default: m.PriceGraph })));
const PriceMatrix = React.lazy(() => import('../../PriceMatrix/components/PriceMatrix'));
const SearchFormDatepicker = React.memo(forwardRef((props, ref) => {
    var _a, _b;
    const DatepickerOverride = (_a = useRenderers().DatepickerInsideSearchForm) !== null && _a !== void 0 ? _a : Datepicker;
    const { t } = useTranslation('SearchFormDatepicker');
    const [tab, setTab] = useState(TabTypes.PlainCalendar), [isOpen, toggleOpen] = useState(false), outDate = props.selectedDates && props.selectedDates.length ? new Date(props.selectedDates[0]) : null, returnDate = props.selectedDates && props.selectedDates.length > 1 ? new Date(props.selectedDates[1]) : null, isTabDisplay = props.showPriceMatrix || props.showPriceGraph, isMobile = useMediaQuery({ maxWidth: +MOBILE_MIN_WIDTH + 1 }), isTablet = useMediaQuery({ maxWidth: 1024 }), isNarrowDesktop = useMediaQuery({ maxWidth: 1160 });
    const isMultyCity = useSelector(isMultiCity);
    const { SearchFormDatepickerCompactMoney: SearchFormDatepickerCompactMoneyOverride = CompactMoney } = useRenderers();
    const { SearchForm: { separatedDatepicker } } = useConfig();
    const showButtons = !separatedDatepicker || isTablet || !!outDate;
    const refEl = useRef(null);
    const contentRef = React.createRef();
    const onClose = () => {
        toggleOpen(false);
        props.clearOpenDate && props.clearOpenDate();
        if (props.onClose) {
            props.onClose();
        }
    };
    const onDone = () => {
        toggleOpen(false);
        if (props.onDone) {
            props.onDone();
        }
    };
    const open = () => {
        var _a;
        toggleOpen(true);
        (_a = props.onOpen) === null || _a === void 0 ? void 0 : _a.call(props);
    };
    const onSelect = (date) => {
        if (props.onSelect) {
            props.onSelect(date, props.routeType === 'return');
        }
        if (separatedDatepicker) {
            toggleOpen(false);
        }
        if (props.closeAfterSelectReturnDate && props.closeAfterSelectReturnDate(date)) {
            toggleOpen(false);
        }
    };
    const { Datepicker: searchFormDatepickerTheme } = useTheme('SearchForm');
    const _c = useTheme('SearchFormDatepicker'), { SearchFormDatepickerStyles: datepickerTheme } = _c, defaultDatepickerTheme = __rest(_c, ["SearchFormDatepickerStyles"]);
    let showOverlay = false;
    if (props.routeType === DatepickerTab.Outbound && props.pricesTo === null) {
        showOverlay = true;
    }
    else if (props.routeType === DatepickerTab.Return && (props.pricesTo === null || props.pricesBack === null)) {
        showOverlay = true;
    }
    if (!isMobile) {
        showOverlay = false;
    }
    const pricesLoaded = () => {
        if (props.routeType === 'outbound' && Object.keys(props.pricesTo).length) {
            return true;
        }
        else if (props.routeType === 'return' &&
            Object.keys(props.pricesTo).length &&
            Object.keys(props.pricesBack).length) {
            return true;
        }
        return false;
    };
    const datepickerStyles = useTheme('Datepicker');
    const legendStyles = useTheme('Features').PriceGraph.LegendsStyles;
    const datePickerWithPricesTheme = {
        Datepicker: Object.assign(Object.assign({}, defaultDatepickerTheme), { WeeklyHeaderStyles: Object.assign(Object.assign({}, defaultDatepickerTheme.WeeklyHeaderStyles), { dayOfWeekName: defaultDatepickerTheme.WeeklyHeaderStyles.dayOfWeekName_withPrices }), MonthStyles: Object.assign(Object.assign({}, defaultDatepickerTheme.MonthStyles), { week: defaultDatepickerTheme.MonthStyles.week_withPrice }), DayStyles: Object.assign(Object.assign({}, defaultDatepickerTheme.DayStyles), { day: defaultDatepickerTheme.DayStyles.day_withPrice, day_endHover: defaultDatepickerTheme.DayStyles.day_endHover_withPrice, day_startHover: defaultDatepickerTheme.DayStyles.day_startHover_withPrice }) })
    };
    const customStyles = {
        Datepicker: Object.assign(Object.assign({}, defaultDatepickerTheme), { DayStyles: Object.assign(Object.assign({}, defaultDatepickerTheme.DayStyles), { day: props.datepickerAlternativeHighlighting
                    ? datepickerTheme.day
                    : defaultDatepickerTheme.DayStyles.day }), MonthsWrapperStyles: Object.assign(Object.assign({}, defaultDatepickerTheme.MonthsWrapperStyles), { wrapper: defaultDatepickerTheme.MonthsWrapperStyles.wrapper, nav_prev: defaultDatepickerTheme.MonthsWrapperStyles.nav_prev, nav_next: defaultDatepickerTheme.MonthsWrapperStyles.nav_next }) })
    };
    const defaultStyles = {
        Datepicker: Object.assign({}, datepickerStyles)
    };
    // todo: react.memo does not make deep compare, if we want to memoise mergedTheme, we can pass JSON.stringify(defaultStyles), JSON.strignify(customStyles) do deps array
    const mergedTheme = React.useMemo(() => mergeCSS(defaultStyles, props.useThemeWithPrices ? datePickerWithPricesTheme : customStyles), [defaultStyles, customStyles]);
    const dayWithPrice = (date) => {
        var _a, _b, _c;
        const compareString = date.toISOString();
        let price = null;
        const pricesMap = props.routeType === 'outbound' ? props.pricesTo : props.pricesBack;
        const hasFlight = (_a = pricesMap === null || pricesMap === void 0 ? void 0 : pricesMap[compareString]) === null || _a === void 0 ? void 0 : _a.hasFlight;
        if (pricesMap && pricesMap[compareString] && ((_c = (_b = pricesMap[compareString]) === null || _b === void 0 ? void 0 : _b.price) === null || _c === void 0 ? void 0 : _c.amount)) {
            price = pricesMap[compareString].price;
        }
        return (React.createElement("div", { className: cn({ [defaultDatepickerTheme.DayStyles.day_hasPrice]: price || hasFlight }) },
            date.getDate(),
            price && (React.createElement(SearchFormDatepickerCompactMoneyOverride, { moneyClassName: cn(defaultDatepickerTheme.DayStyles.day__price, searchFormDatepickerTheme.day__price), money: price }))));
    };
    const handleOverlayButtonClick = React.useCallback(() => {
        setTab(TabTypes.PlainCalendar);
    }, []);
    const monthsHeader = !isMultyCity && !returnDate ? (React.createElement("div", { className: cn(datepickerTheme.selectDateHint, {
            [datepickerTheme.selectDateHint_return]: !!outDate
        }) }, t(!!outDate ? 'Select the return flight date' : 'Select the depart flight date'))) : null;
    const buttonRendererProps = {
        notActive: props.isClearable,
        onClear: props.onClear,
        onClose,
        isDoneable: props.isDoneable,
        onDone,
        tab,
        routeType: props.routeType,
        isDateBackSelected: props.isDateBackSelected,
        isDateToSelected: props.isDateToSelected,
        isMultyCity
    };
    const desktopMonthCount = isNarrowDesktop ? 2 : (_b = props.monthCount) !== null && _b !== void 0 ? _b : 3;
    const { isOutDateInPast, normalizedSelectedDates, normalizedOpenDate } = useCalendarNormalization(outDate, props.selectedDates, props.openDate, isMobile, desktopMonthCount);
    React.useEffect(() => {
        if (isOpen && isOutDateInPast && props.onSelect) {
            const today = startOfDay(new Date());
            props.onSelect(today, false);
        }
    }, [isOpen, isOutDateInPast, props.onSelect]);
    return (React.createElement(ThemeProvider, { value: mergedTheme },
        React.createElement("div", { ref: ref, className: datepickerTheme.inputWrapper },
            React.createElement("input", Object.assign({ placeholder: props.inputPlaceholder, className: classnames(props.inputClassName, { [props.inputFocusedClassName]: isOpen }), onClick: open, ref: refEl, value: props.inputValue, readOnly: true }, disableAutocompleteProps)),
            props.valueRenderer && props.valueRenderer(isOpen, open, onClose)),
        React.createElement(DialogAdapter, { onClose: onClose, isOpen: isOpen, outsideClickIgnoreClass: props.outsideClickIgnoreClass, popupClassName: datepickerTheme.dialog },
            React.createElement("div", { className: classnames(datepickerTheme.popup, {
                    [datepickerTheme.popup_withoutTabs]: !isTabDisplay
                }), "data-search-form-datepicker": true },
                props.showProgress && (React.createElement(LinearProgress, { variant: "indeterminate", classes: {
                        root: datepickerTheme.progress__root,
                        barColorPrimary: datepickerTheme.progress__bar__color,
                        colorPrimary: datepickerTheme.progress__color
                    } })),
                React.createElement(MediaQuery, { maxWidth: TABLET_MIN_WIDTH },
                    React.createElement("div", { className: datepickerTheme.dialog__bar },
                        React.createElement("div", { className: datepickerTheme.dialog__dates, onClick: onClose },
                            React.createElement("span", { className: classnames({
                                    [datepickerTheme.dialog__activeDate]: props.routeType === 'outbound'
                                }), onClick: e => {
                                    e.stopPropagation();
                                    props.onClickOutboundDate(e);
                                } },
                                React.createElement("span", null, outDate
                                    ? removeDotsFromDate(format(outDate, 'd MMM, EEEEEE'))
                                    : t('Outbound'))),
                            outDate && (React.createElement("span", { className: classnames(datepickerTheme.dialog__dateBack, {
                                    [datepickerTheme.dialog__activeDate]: props.routeType === 'return'
                                }), onClick: e => {
                                    e.stopPropagation();
                                    props.onClickReturnDate(e);
                                } },
                                React.createElement("span", null, returnDate
                                    ? removeDotsFromDate(format(returnDate, 'd MMM, EEEEEE'))
                                    : t('Return'))))),
                        React.createElement("div", { onClick: onClose, className: datepickerTheme.dialog__close }))),
                React.createElement("div", { className: classnames(datepickerTheme.content), ref: contentRef },
                    tab === TabTypes.PlainCalendar && (React.createElement(React.Fragment, null,
                        React.createElement(MediaQuery, { minWidth: +TABLET_MIN_WIDTH + 1 },
                            React.createElement(DatepickerOverride, { onSelect: onSelect, onHover: props.onHover, hoverDates: props.hoverDates, openDate: normalizedOpenDate, highlightedDates: props.highlightedDates, selectedDates: normalizedSelectedDates, monthCount: desktopMonthCount, minDate: props.minDate, maxDate: props.maxDate, dayRenderer: props.useThemeWithPrices && dayWithPrice, monthsHeader: monthsHeader })),
                        React.createElement(MediaQuery, { maxWidth: TABLET_MIN_WIDTH },
                            React.createElement(DatepickerOverride, { getParentRef: () => contentRef, onSelect: onSelect, onHover: props.onHover, hoverDates: props.hoverDates, openDate: normalizedOpenDate, highlightedDates: props.highlightedDates, selectedDates: normalizedSelectedDates, monthCount: 12, monthBaseDateVariant: "min", minDate: props.minDate, maxDate: props.maxDate, dayRenderer: props.useThemeWithPrices && dayWithPrice })))),
                    tab === TabTypes.PriceGraph &&
                        (showOverlay ? (React.createElement(EmptyDatesOverlay, { onClick: handleOverlayButtonClick })) : (React.createElement(ThemeProvider, { value: {
                                Features: {
                                    PriceGraph: Object.assign(Object.assign({}, priceGraphTheme), { LegendsStyles: legendStyles })
                                }
                            } },
                            React.createElement(React.Suspense, { fallback: React.createElement("p", null, "...") }, props.showPriceGraph && pricesLoaded() ? (React.createElement(PriceGraph, { outDate: outDate, backDate: returnDate, openDate: props.selectedDates && props.selectedDates.length
                                    ? props.selectedDates[0]
                                    : props.openDate, onPricesEnd: props.onPriceGraphPricesEnd, onSelect: onSelect, prices: props.routeType === 'return' ? props.pricesBack : props.pricesTo, routeType: props.routeType === 'return'
                                    ? RouteType.Return
                                    : RouteType.Outbound })) : (React.createElement(DummyPriceGraph, null)))))),
                    tab === TabTypes.RoundtripMatrix && (React.createElement(PriceMatrixProvider, { value: priceMatrixTheme },
                        React.createElement(React.Suspense, { fallback: React.createElement("p", null, "...") },
                            React.createElement(PriceMatrix, { prices: props.matrixPrices, size: 7, onDateSelect: props.onRTSelect }))))),
                React.createElement("div", { className: datepickerTheme.actionButtons },
                    props.useThemeWithPrices && (React.createElement(MediaQuery, { minWidth: TABLET_MIN_WIDTH },
                        React.createElement("div", { className: datepickerTheme.datepicker__disclaimer },
                            React.createElement(CdnIcon, { icon: "Info/Details" }),
                            React.createElement("span", { className: datepickerTheme.datepicker__disclaimerText }, t('Approximate prices for 1 person for 1 direction'))))),
                    React.createElement(MediaQuery, { minWidth: TABLET_MIN_WIDTH },
                        React.createElement(FooterButtons, { showButtons: showButtons, outDate: outDate, returnDate: returnDate, buttonRendererProps: buttonRendererProps }))),
                React.createElement("div", { className: datepickerTheme.footer }, isTabDisplay && (React.createElement(Tabs, { value: tab, onChange: (ecent, newValue) => setTab(newValue), classes: {
                        root: datepickerTheme.tabs,
                        indicator: datepickerTheme.tab__indicator
                    } },
                    React.createElement(Tab, { disableRipple: true, classes: {
                            root: datepickerTheme.tab,
                            wrapped: datepickerTheme.tab__wrapper,
                            selected: datepickerTheme.tab_selected
                        }, label: t('Calendar'), icon: React.createElement(MediaQuery, { maxWidth: TABLET_MIN_WIDTH },
                            React.createElement(CdnIcon, { icon: "Action/Show Calendar" })) }),
                    props.showPriceGraph && (React.createElement(Tab, { disableRipple: true, classes: {
                            root: datepickerTheme.tab,
                            wrapped: datepickerTheme.tab__wrapper,
                            selected: datepickerTheme.tab_selected
                        }, label: t('Price graph'), icon: React.createElement(MediaQuery, { maxWidth: TABLET_MIN_WIDTH },
                            React.createElement(CdnIcon, { icon: "Action/Show Price Graph" })) })),
                    props.showPriceMatrix && (React.createElement(Tab, { disableRipple: true, classes: {
                            root: classnames(datepickerTheme.tab, datepickerTheme.tab_matrix),
                            selected: datepickerTheme.tab_selected
                        }, label: t('Price matrix') }))))),
                React.createElement(MediaQuery, { maxWidth: TABLET_MIN_WIDTH - 1 },
                    React.createElement(FooterButtons, { showButtons: showButtons, outDate: outDate, returnDate: returnDate, buttonRendererProps: buttonRendererProps }))))));
}));
export default SearchFormDatepicker;
